import React, { useEffect, useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Alert, Button, Modal, Space, Spin, Tag, Typography, notification } from 'antd';
import { ReloadOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';

import API from 'services/api/index';
import {
  invalidateStayingGuestsReport,
  stayingGuestsQueryKeys,
  StayingGuestsFilterParams
} from 'services/api/stayingGuestsQueryKeys';
import { StayingGuestType } from 'services/api/type/report.type';
import {
  STAYING_GUEST_XML_FIELD_LABELS,
  validateStayingGuestsXmlExport
} from 'utils/stayingGuestsXmlValidation';
import { useOpenBookingDetail } from 'hooks/useOpenBookingDetail';

import 'styles/staying-guests-xml-validation-modal.scss';

type Props = {
  open: boolean;
  filters: StayingGuestsFilterParams | null;
  onClose: () => void;
};

function formatGuestRef(row: StayingGuestType): string {
  const parts: string[] = [];
  if (row.booking_id) parts.push(String(row.booking_id));
  if (row.booking_line_id) parts.push(String(row.booking_line_id));
  parts.push(row.room_name || '—');
  return parts.join(' · ');
}

function StayingGuestsXmlValidationModal({ open, filters, onClose }: Props) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { openBookingDetail } = useOpenBookingDetail();
  const activeFilters = open && filters != null ? filters : null;

  const { data, isFetching, refetch } = useQuery({
    queryKey: activeFilters ? stayingGuestsQueryKeys.xmlValidation(activeFilters) : ['disabled'],
    queryFn: () => {
      if (!activeFilters) {
        throw new Error('Staying guest filters are required');
      }
      return API.report.getStayingGuests({
        start_date: activeFilters.start_date,
        end_date: activeFilters.end_date,
        status: activeFilters.status,
        country: activeFilters.country ?? ''
      });
    },
    enabled: activeFilters != null
  });

  const rows = useMemo(() => {
    return ((data as { data?: StayingGuestType[] } | undefined)?.data ?? []) as StayingGuestType[];
  }, [data]);
  const validationResult = useMemo(
    () =>
      rows.length > 0 ? validateStayingGuestsXmlExport(rows) : { ok: true, issues: [], total: 0 },
    [rows]
  );

  const handleReload = async () => {
    await invalidateStayingGuestsReport(queryClient);
    await refetch();
  };

  useEffect(() => {
    if (!open || !validationResult.ok) return;
    if (rows.length === 0) return;
    notification.success({
      message: t('managePage.caDeclarationPage.xmlValidation.allReady')
    });
    invalidateStayingGuestsReport(queryClient);
    onClose();
  }, [open, validationResult.ok, rows.length, queryClient, onClose, t]);

  return (
    <Modal
      open={open}
      title={t('managePage.caDeclarationPage.xmlValidation.title')}
      onCancel={onClose}
      footer={
        <Space>
          <Button icon={<ReloadOutlined />} loading={isFetching} onClick={handleReload}>
            {t('common.actions.refresh')}
          </Button>
          <Button type="primary" className="ant-btn-secondary" onClick={onClose}>
            {t('common.actions.cancel')}
          </Button>
        </Space>
      }
      width={640}
      destroyOnClose
      className="staying-guests-xml-validation-modal"
      zIndex={900}
    >
      <Alert
        type="warning"
        showIcon
        className="staying-guests-xml-validation-modal__alert"
        message={t('managePage.caDeclarationPage.xmlValidation.description', {
          missing: validationResult.issues.length,
          total: validationResult.total
        })}
      />

      {isFetching ? (
        <div className="staying-guests-xml-validation-modal__loading">
          <Spin size="small" />
          <Typography.Text type="secondary">
            {t('managePage.caDeclarationPage.xmlValidation.refreshing')}
          </Typography.Text>
        </div>
      ) : (
        <div className="staying-guests-xml-validation-modal__list">
          {validationResult.issues.map(({ row, missingFields }) => {
            const bookingLineId = row.booking_line_id;
            return (
              <div
                key={`${row.id}-${row.room_id}`}
                className="staying-guests-xml-validation-modal__item"
              >
                <div className="staying-guests-xml-validation-modal__item-main">
                  <div className="staying-guests-xml-validation-modal__item-content">
                    <div className="staying-guests-xml-validation-modal__name">{row.name}</div>
                    <div className="staying-guests-xml-validation-modal__meta">
                      {formatGuestRef(row)}
                    </div>
                    <div className="staying-guests-xml-validation-modal__tags">
                      {missingFields.map(field => (
                        <Tag key={field} color="error" bordered={false}>
                          {STAYING_GUEST_XML_FIELD_LABELS[field]}
                        </Tag>
                      ))}
                    </div>
                  </div>
                  {bookingLineId ? (
                    <div className="staying-guests-xml-validation-modal__actions">
                      <Button size="small" onClick={() => openBookingDetail(bookingLineId)}>
                        {t('managePage.caDeclarationPage.xmlValidation.openBooking')}
                      </Button>
                    </div>
                  ) : null}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </Modal>
  );
}

export default StayingGuestsXmlValidationModal;
